import {
    ICountdownWidgetData, ICountdownLabelsStyle, ICountdownWidgetStyle, ICountdownDigitsStyle,
    ICountdownDelimitersStyle, IBorderRadiusData
} from "@pageBuilder/typings";
import { Base64Util } from "../../utils/base64-util";
import { StringUtil } from "../../utils/string-util";
import { CssProperties } from "../../models/css-property";
import { WidgetRendererBase } from "./widget-renderer-base";

export class CountdownWidgetRenderer extends WidgetRendererBase<ICountdownWidgetData> {

    protected createNewInternal(): HTMLElement {
        const element = this.createWidgetElement("countdownWidget");
        element.classList.add("countdown");

        return element;
    }

    private getScriptId(elementId: string): string {
        return `${elementId}-generated-scripts`;
    }

    protected renderInternal(element: HTMLElement, data: ICountdownWidgetData): HTMLElement {
        const json = Base64Util.serialize(data) || "";
        element.setAttribute("data-countdown", json);

        if (this.isEditorRendering) {
            element.setAttribute("draft-countdown", "true");
        }

        this.ensureCountdown(element, data);

        this.renderStyles(element, data);

        element.appendChild(this.createScript(element.id, data));

        return element;
    }

    protected renderStyles(element: HTMLElement, data: ICountdownWidgetData): void {
        this.renderBaseStyles(element, data.style);
    }

    private renderBaseStyles(element: HTMLElement, style: ICountdownWidgetStyle): void {
        if (style) {
            this.setDigitStyle(element, style.digitsStyle);
            this.setDelimiterStyle(element, style);
            this.setLabelsStyle(element, style.labelsStyle);
        }
        this.animationRenderer.setAnimationStyles(element, style.animation);
    }

    private ensureCountdown(element: HTMLElement, data: ICountdownWidgetData): void {
        const content = element.querySelector(".widget-content") as HTMLElement;
        let countdownElement = element.querySelector<HTMLElement>(".countdown-container");
        if (!countdownElement) {
            countdownElement = document.createElement("div");
            countdownElement.classList.add("countdown-container");

            content.appendChild(countdownElement);
        } else {
            this.cleanupCountdownContainer(countdownElement);
            const script = document.getElementById(this.getScriptId(element.id));
            if (script) {
                script.remove();
            }
        }

        if (data.style) {
            this.setPrecision(countdownElement, data);
        }
    }

    private cleanupCountdownContainer(countdownContainer: HTMLElement): void {
        while (countdownContainer.firstChild) {
            countdownContainer.removeChild(countdownContainer.firstChild);
        }
    }

    private setPrecision(countdownContainer: HTMLElement, data: ICountdownWidgetData): void {
        const CountdownDate = Date.UTC(data.year, data.month, data.date, data.hours, data.minutes, data.seconds);
        const timezoneDate = new Date().getTime() + data.timezone.offset * 60 * 1000;
        const timestamp = CountdownDate - timezoneDate;
        const days = Math.floor(timestamp / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timestamp % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timestamp % (1000 * 60 * 60)) / (1000 * 60));

        switch (data?.style?.precision) {
            case 1:
                if (days > 0) {
                    countdownContainer.appendChild(this.createElement(data, "days"));
                } else if (hours > 0) {
                    countdownContainer.appendChild(this.createElement(data, "hours"));
                } else if (minutes > 0) {
                    countdownContainer.appendChild(this.createElement(data, "minutes"));
                } else {
                    countdownContainer.appendChild(this.createElement(data, "seconds"));
                }
                break;
            case 2:
                if (days > 0) {
                    countdownContainer.appendChild(this.createElement(data, "days"));
                }
                if (hours > 0) {
                    countdownContainer.appendChild(this.createElement(data, "hours"));
                }
                if (days <= 0) {
                    countdownContainer.appendChild(this.createElement(data, "minutes"));
                }
                if (days <= 0 && hours <= 0) {
                    countdownContainer.appendChild(this.createElement(data, "seconds"));
                }
                break;
            case 3:
                if (days > 0) {
                    countdownContainer.appendChild(this.createElement(data, "days"));
                }
                countdownContainer.appendChild(this.createElement(data, "hours"));
                countdownContainer.appendChild(this.createElement(data, "minutes"));
                if (days <= 0) {
                    countdownContainer.appendChild(this.createElement(data, "seconds"));
                }
                break;
            case 4:
                countdownContainer.appendChild(this.createElement(data, "days"));
                countdownContainer.appendChild(this.createElement(data, "hours"));
                countdownContainer.appendChild(this.createElement(data, "minutes"));
                countdownContainer.appendChild(this.createElement(data, "seconds"));
                break;
        }

    }

    private createElement(data: ICountdownWidgetData, type: "days" | "hours" | "minutes" | "seconds"): HTMLElement {
        const div = document.createElement("div");
        div.classList.add(type, "countdown-slot");

        const divContent = document.createElement("div");
        divContent.classList.add("countdown-slot-content");
        const divValue = document.createElement("div");
        divValue.classList.add("countdown-value");
        divValue.innerText = "00";
        const divUnit = document.createElement("div");
        divUnit.classList.add("countdown-unit");

        if (data.style && data.labelsData && data.style.labelsStyle.type !== "none") {
            let text = data.labelsData[type];
            switch (data.style.labelsStyle.appearance) {
                case "lowercase":
                    text = text.toLocaleLowerCase();
                    break;
                case "uppercase":
                    text = text.toLocaleUpperCase();
                    break;
            }
            divUnit.innerText = text;
        }
        divContent.appendChild(divValue);
        divContent.appendChild(divUnit);
        div.appendChild(divContent);
        return div;
    }

    private setDigitStyle(element: HTMLElement, digitsStyle: ICountdownDigitsStyle): void {
        const digitsProps = new CssProperties();
        digitsProps.tryAdd("color", digitsStyle.color);
        digitsProps.tryAdd("font-family", digitsStyle.font?.family);
        digitsProps.tryAdd("font-weight", digitsStyle.font?.weight);
        digitsProps.tryAdd("font-size", digitsStyle.font?.size);
        digitsProps.tryAdd("letter-spacing", digitsStyle.font?.letterSpacing);
        digitsProps.tryAdd("display", "flex");
        digitsProps.tryAdd("box-sizing", "border-box");
        digitsProps.tryAdd("align-items", "center");
        digitsProps.tryAdd("justify-content", "center");

        if (digitsStyle.type === "solid") {
            this.setStyleForSolid(digitsProps, digitsStyle);
        }

        if (digitsStyle.type === "outline") {
            this.setStyleForOutline(digitsProps, digitsStyle);
        }

        this.styleService.setStyle(element, `#${element.id} .countdown-value`, digitsProps.data);
    }

    private setStyleForSolid(digitsProps: CssProperties, digitsStyle: ICountdownDigitsStyle): void {
        if (digitsStyle.font?.letterSpacing) {
            const spacing = StringUtil.getNumberFromStr(digitsStyle.font?.letterSpacing);

            digitsProps.tryAdd("padding", `${18 + spacing}px`);
            digitsProps.tryAdd("padding-right", `18px`);
            if (digitsStyle.shape.type === "rectangle") {
                digitsProps.tryAdd("background-color", digitsStyle.shape.color);
                this.setBordersRadius(digitsProps, digitsStyle.shape.borderRadius);
            }
            if (digitsStyle.shape.type === "circle") {
                digitsProps.tryAdd("background-color", digitsStyle.shape.color);
                digitsProps.tryAdd("border-radius", "50%");
            }
        }
    }

    private setStyleForOutline(digitsProps: CssProperties, digitsStyle: ICountdownDigitsStyle): void {
        if (digitsStyle.font?.letterSpacing) {
            const spacing = StringUtil.getNumberFromStr(digitsStyle.font?.letterSpacing);

            digitsProps.tryAdd("padding", `${18 + spacing}px`);
            digitsProps.tryAdd("padding-right", `18px`);
            if (digitsStyle.shape.type === "rectangle") {
                digitsProps.tryAdd("border", `1px solid ${digitsStyle.shape.color}`);
                this.setBordersRadius(digitsProps, digitsStyle.shape.borderRadius);
            }
            if (digitsStyle.shape.type === "circle") {
                digitsProps.tryAdd("border", `1px solid ${digitsStyle.shape.color}`);
                digitsProps.tryAdd("border-radius", "50%");
            }
        }
    }

    private setBordersRadius(digitsProps: CssProperties, borderRadius: IBorderRadiusData | null | undefined): void {
        if (borderRadius) {
            digitsProps.tryAdd("border-top-left-radius", `${borderRadius.topLeftPx}px`);
            digitsProps.tryAdd("border-top-right-radius", `${borderRadius.topRightPx}px`);
            digitsProps.tryAdd("border-bottom-left-radius", `${borderRadius.bottomLeftPx}px`);
            digitsProps.tryAdd("border-bottom-right-radius", `${borderRadius.bottomRightPx}px`);
        }
    }

    private setDelimiterStyle(element: HTMLElement, style: ICountdownWidgetStyle): void {
        this.styleService.clearStyle(element, `#${element.id} .countdown-slot`);
        this.styleService.clearStyle(element, `#${element.id} .countdown-slot::after`);
        this.styleService.clearStyle(element, `#${element.id} .countdown-slot:last-child::after`);
        this.styleService.clearStyle(element, `#${element.id} .countdown-slot:last-child`);

        switch (style?.delimitersStyle.type) {
            case "lines":
                this.setLinesStyle(element, style.delimitersStyle);
                break;
            case "colons":
                this.setColonsStyle(element, style);
                break;
        }
    }

    private setLinesStyle(element: HTMLElement, delimitersStyle: ICountdownDelimitersStyle): void {
        const lineProps = new CssProperties();
        lineProps.tryAdd("border-right", `1px solid ${delimitersStyle?.color}`);
        lineProps.tryAdd("padding-right", `4px`);
        this.styleService.setStyle(element, `#${element.id} .countdown-slot`, lineProps.data);
        this.styleService.setStyleObject(element, `#${element.id} .countdown-slot:last-child`, {
            "border-right": "none"
        });
    }

    private setColonsStyle(element: HTMLElement, style: ICountdownWidgetStyle): void {
        const colonsStyle = {
            "align-self": "flex-start",
            "color": style?.delimitersStyle?.color,
            "font-size": style?.digitsStyle?.font?.size,
            "font-family": style?.digitsStyle?.font?.family,
            "content": "\":\"",
        };

        if (style && style?.digitsStyle?.font?.letterSpacing) {
            const letterSpacing = StringUtil.getNumberFromStr(style?.digitsStyle?.font?.letterSpacing);
            colonsStyle["letter-spacing"] = `${letterSpacing}px`;
            if (style.digitsStyle.type !== "default") {
                colonsStyle["padding"] = `${18 + letterSpacing}px`;
                colonsStyle["padding-right"] = `18px`;
            }
        }

        this.styleService.setStyleObject(element, `#${element.id} .countdown-slot::after`, colonsStyle);
        this.styleService.setStyleObject(element, `#${element.id} .countdown-slot:last-child::after`, {
            content: "\"\""
        });
    }

    private setLabelsStyle(element: HTMLElement, style: ICountdownLabelsStyle): void {
        const labelsProps = new CssProperties();
        labelsProps.tryAdd("color", style?.color);
        labelsProps.tryAdd("font-size", style?.font?.size);
        labelsProps.tryAdd("font-family", style?.font?.family);
        labelsProps.tryAdd("font-weight", style?.font?.weight);
        labelsProps.tryAdd("letter-spacing", style?.font?.letterSpacing);
        labelsProps.tryAdd("text-align", "center");
        labelsProps.tryAdd("word-break", "break-all");
        labelsProps.tryAdd("font-variant", style?.appearance === "small-caps" ? "small-caps" : "normal");

        this.styleService.setStyle(element, `#${element.id} .countdown-unit`, labelsProps.data);
    }

    private createScript(id: string, countdown: ICountdownWidgetData): HTMLElement {
        const countdownDate = Date.UTC(countdown.year, countdown.month, countdown.date, countdown.hours, countdown.minutes, countdown.seconds);
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.id = this.getScriptId(id);
        script.textContent = `
        updateCountdownSlots("${id}", ${countdownDate}, ${countdown.timezone.offset}, "${countdown.url}");

        var countdown_${id.replace("-", "_")} = runInterval("${id}");

        function runInterval(id) {
            const element = document.getElementById(id);
            if (element && !element.hasAttribute("draft-countdown")) {
                clearInterval(countdown_${id.replace("-", "_")});
                return window.setInterval(updateCountdownSlots, 1000, "${id}", ${countdownDate}, ${countdown.timezone.offset}, "${countdown.url}");
            }

            return null;
        }

        function updateCountdownSlots(id, date, offset, url) {
            const element = document.getElementById(id);

            if (!element) { return; }

            let timestamp = date - (new Date().getTime() + offset * 60 * 1000);
            if (timestamp < 0 && "${countdown.type}" === "CountdownWidget") {
                clearInterval(countdown_${id.replace("-", "_")});
                if (!element.hasAttribute("draft-Countdown") && url && "${countdown.countdownType}" === "countdown") {
                    window.setTimeout(function () {
                        window.location.href = url;
                    }, 1000);
                }
            } else {
                timestamp = Math.abs(timestamp);
            }

            this.daysElement = element.querySelector(".days .countdown-value");
            this.hoursElement = element.querySelector(".hours .countdown-value");
            this.minutesElement = element.querySelector(".minutes .countdown-value");
            this.secondsElement = element.querySelector(".seconds .countdown-value");

            if (daysElement) {
                daysElement.innerText = formatNumber(Math.floor(timestamp / (1000 * 60 * 60 * 24)));
            }
            if (hoursElement) {
                hoursElement.innerText = formatNumber(Math.floor((timestamp % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)));
            }
            if (minutesElement) {
                minutesElement.innerText = formatNumber(Math.floor((timestamp % (1000 * 60 * 60)) / (1000 * 60)));
            }
            if (secondsElement) {
                secondsElement.innerText = formatNumber(Math.floor((timestamp % (1000 * 60)) / 1000));
            }

            function formatNumber(value) {
                if (!value || value < 0) {
                    return "00";
                }
                const item = Math.trunc(value);
                if (item > 9) {
                    return item.toString();
                } else {
                    return "0" + item.toString();
                }
            }
        }
        `;
        return script;
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [
            `#${element.id}`,
            `#${element.id} .countdown-container`, `#${element.id} .countdown-slot`,
            `#${element.id} .countdown-slot-content`, `#${element.id} .countdown-slot::after`,
            `#${element.id} .countdown-slot:last-child::after`, `#${element.id} .countdown-slot:last-child`,
            `#${element.id} .countdown-unit`, `#${element.id} .countdown-value`
        ];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

}
